import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  Card,
  Table,
  Typography,
  Breadcrumb,
  Alert,
  Space,
  Tag,
  Statistic,
  Row,
  Col,
} from 'antd';
import apiClient from '@/services/api';

const { Title } = Typography;

interface PlanSummary {
  planIdx: string;
  planName: string | null;
  period: string | null;
  periodUnit: number | null;
  quantity: number | null;
  planExpired: string | null;
  activeCount: number;
  suspendedCount: number;
  expiredCount: number;
  totalCount: number;
}

interface Resp {
  Result: string;
  vendorId: string;
  plans: PlanSummary[];
}

export default function DistributorVendorDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['distributor', 'vendor', id, 'summary'],
    enabled: !!id,
    queryFn: async () => {
      const r = await apiClient.get<Resp>(`/distributor/vendors/${id}/licenses/summary`);
      return r as unknown as Resp;
    },
  });

  const totals = (data?.plans ?? []).reduce(
    (a, p) => ({
      active: a.active + p.activeCount,
      suspended: a.suspended + p.suspendedCount,
      expired: a.expired + p.expiredCount,
      total: a.total + p.totalCount,
    }),
    { active: 0, suspended: 0, expired: 0, total: 0 },
  );

  return (
    <>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/distributor">Vendors</Link> },
          { title: id ? <code>{id.slice(0, 8)}…</code> : '—' },
        ]}
      />
      <Title level={3} style={{ marginTop: 0 }}>
        {t('distributor.vendorDetail')}
      </Title>

      {error ? (
        <Alert type="error" message="Failed to load summary" description={String(error)} />
      ) : (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Card>
                <Statistic title="Active units" value={totals.active} loading={isLoading} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Suspended"
                  value={totals.suspended}
                  valueStyle={{ color: '#d46b08' }}
                  loading={isLoading}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Expired"
                  value={totals.expired}
                  valueStyle={{ color: '#999' }}
                  loading={isLoading}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic title="Total issued" value={totals.total} loading={isLoading} />
              </Card>
            </Col>
          </Row>

          <Card title="Plans">
            <Table<PlanSummary>
              size="small"
              rowKey="planIdx"
              loading={isLoading}
              dataSource={data?.plans ?? []}
              pagination={false}
              columns={[
                { title: 'Plan', dataIndex: 'planName', key: 'name' },
                {
                  title: 'Period',
                  key: 'period',
                  render: (_, r) =>
                    r.period ? `${r.periodUnit ?? 1} ${r.period}` : '—',
                  width: 130,
                },
                {
                  title: 'Quantity',
                  dataIndex: 'quantity',
                  key: 'q',
                  width: 100,
                },
                {
                  title: 'Plan expiry',
                  dataIndex: 'planExpired',
                  key: 'pe',
                  width: 150,
                  render: (v: string | null) => (v ? new Date(v).toLocaleDateString() : '—'),
                },
                {
                  title: 'Active',
                  dataIndex: 'activeCount',
                  key: 'ac',
                  width: 80,
                  render: (v: number) => (
                    <Tag color={v > 0 ? 'green' : 'default'}>{v}</Tag>
                  ),
                },
                {
                  title: 'Suspended',
                  dataIndex: 'suspendedCount',
                  key: 'sc',
                  width: 100,
                  render: (v: number) => (
                    <Tag color={v > 0 ? 'orange' : 'default'}>{v}</Tag>
                  ),
                },
                {
                  title: 'Expired',
                  dataIndex: 'expiredCount',
                  key: 'ec',
                  width: 80,
                  render: (v: number) => <Tag>{v}</Tag>,
                },
                {
                  title: 'Total',
                  dataIndex: 'totalCount',
                  key: 'tc',
                  width: 80,
                },
              ]}
            />
          </Card>

          <Space style={{ marginTop: 16 }}>
            <Alert
              type="info"
              showIcon
              message="Unit-level DeviceUUID is hidden from distributor view to protect end customers."
            />
          </Space>
        </>
      )}
    </>
  );
}
